import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, LinearProgress, Chip, Alert } from '@mui/material';
import { sessionService } from '../../services/sessionService';
import type { ContextUsage } from '../../types/contextWindow';

export interface ContextUsageIndicatorProps {
  sessionId: string;
  /**
   * Polling interval in milliseconds. Default: 5000ms.
   */
  pollIntervalMs?: number;
}

/** Whether context-usage polling is enabled (controlled via VITE_CONTEXT_USAGE_POLLING_ENABLED). */
const isPollingEnabled = import.meta.env.VITE_CONTEXT_USAGE_POLLING_ENABLED === 'true';

/**
 * Displays context window usage (tokens and utilization) for a session.
 * Polling can be disabled by setting VITE_CONTEXT_USAGE_POLLING_ENABLED=false in the .env file.
 */
export const ContextUsageIndicator = ({ sessionId, pollIntervalMs = 5000 }: ContextUsageIndicatorProps) => {
  const [usage, setUsage] = useState<ContextUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const utilizationPct = useMemo(() => {
    if (!usage) return 0;
    const pct = Math.round((Number(usage.utilization) || 0) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [usage]);

  const progressColor = useMemo(() => {
    if (!usage) return 'primary';
    if (utilizationPct >= 90) return 'error';
    if (utilizationPct >= 75) return 'warning';
    return 'primary';
  }, [usage, utilizationPct]);

  useEffect(() => {
    if (!isPollingEnabled) return;

    let isMounted = true;
    let timer: number | undefined;

    const load = async () => {
      try {
        const data = await sessionService.getContextUsage(sessionId);
        if (!isMounted) return;
        setUsage(data);
        setError(null);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load context usage');
      }
    };

    load();
    timer = window.setInterval(load, pollIntervalMs);

    return () => {
      isMounted = false;
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [sessionId, pollIntervalMs]);

  // When polling is disabled, show a minimal disabled indicator
  if (!isPollingEnabled) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Context usage polling disabled
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Context usage</Typography>
        {usage?.shouldSummarize ? <Chip size="small" color="warning" label="Summarize suggested" /> : null}
      </Box>

      <LinearProgress
        variant="determinate"
        value={utilizationPct}
        color={progressColor as any}
        sx={{ height: 8, borderRadius: 1 }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {usage ? `${usage.totalTokens.toLocaleString()} / ${usage.maxContextTokens.toLocaleString()} tokens` : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {usage ? `${utilizationPct}%` : ''}
        </Typography>
      </Box>

      {usage ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Threshold: {usage.summarizationThreshold.toLocaleString()} · Summary tokens: {usage.summaryTokens.toLocaleString()}
        </Typography>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
    </Box>
  );
};

